// /3d-signs-nyc/ (ProjectServicePage). The page was already code-owned
// (src/pages/3d-signs-nyc.js) but in its own yellow-and-charcoal theme with a
// Typeform; 2026-11-04 moves it into the engraving/printing design family
// and the 3D printing planner, keeping its copy, FAQs and photos.
// GSC Aug–Oct 2026: 2 clicks, 512 impressions, avg position 6. Queries:
// "3d signage in ny" (219 impressions, pos 8.8), "3d signage nyc" (pos 4),
// "custom 3d sign", "dimensional logo signs". Keyword data (US): "3d signage"
// 1,000/mo, "3d signs" 880, "dimensional letters" 390, "custom 3d signs" 170.
// Scope stays fabrication: no licensed sign-company, permit, electrical or
// installation claims (test/3d-signs-nyc.test.js).
const { pricing, reviewedDate, studios, photo } = require('./shared')

const photos = {
  letters: photo(
    '6DZolBxlZ6vjWHtP4XwwDF',
    '73343f50aec84d069ccba2cfdb69020c/LAL_5156.jpg',
    3000,
    2002,
    'Dimensional yellow letters reading THE CHALLENGE, a custom 3D lettering project',
    'Dimensional lettering with a painted finish'
  ),
  window: photo(
    '3E9CrRQJNhzgq4QTz0r5c2',
    'b896cb34d6471434b1fabcda763151df/tiffany-co-display-3d-printed-and-metal-plated.jpg',
    2518,
    1500,
    'A jeweler’s holiday window display with 3D printed and metal-plated elements',
    'Retail window display',
    'Printed and metal-plated pieces for a jeweler’s window.'
  ),
  acrylic: photo(
    '59SD9HOg1lHtgVd3GVBG6y',
    '750c7be260b2dac58e7520af2df1ad34/DSC9453LR__1_.jpg',
    801,
    1200,
    'Laser-cut acrylic artwork mounted on a wall',
    'Laser-cut wall element',
    'Layered laser-cut acrylic, mounted off the wall.'
  ),
  platedLogo: photo(
    'eCoP37zBbagIQe0686Iya',
    '13d117c30d0f9b19ef5300dc99b21ad6/bentley-logo-metal-plated.jpeg',
    960,
    1280,
    'Custom 3D printed logo pieces shown before and after metal plating',
    'Printed logo, plated finish',
    'A 3D printed logo before and after metal plating.'
  ),
}

const page = {
  path: '/3d-signs-nyc/',
  title: 'Custom 3D Signs & 3D Signage NYC | Dimensional Logo Signs',
  description:
    'Custom 3D signs in NYC: dimensional logo signs, raised letters, retail displays and event signs, made in Brooklyn and Queens with 3D printing, CNC and acrylic.',
  heading: 'Custom 3D Signs and 3D Signage in NYC',
  eyebrow: 'Dimensional signage fabricated in Brooklyn & Queens',
  answer:
    'PrintAWorld builds custom 3D signs for New York brands, agencies, designers and production teams: dimensional logo signs, raised letters, retail and window displays, and event signage. Depending on the design, we use 3D printing, CNC routing, laser-cut acrylic, UV printing, paint and metal plating, in our studios in Brooklyn and Glendale, Queens. Send your logo, the size and a photo of the space; each sign is quoted per project after we review it. Pick up by appointment, or we deliver in NYC and ship across the U.S.',
  hero: 'letters',
  reviewedDate,
  crumbs: [{ name: '3D signs NYC' }],
  service: {
    name: 'Custom 3D Signs NYC',
    serviceType: 'Custom 3D signs and dimensional signage',
    areaServed: [
      { '@type': 'City', name: 'New York City' },
      { '@type': 'Country', name: 'United States' },
    ],
    description:
      'Custom 3D signs, dimensional lettering, logo signs and branded display fabrication for New York City businesses, agencies, artists and event teams, using 3D printing, CNC routing, laser-cut acrylic, UV printing, paint and metal plating.',
  },
  quoteLabel: 'Request a 3D sign quote',
}

const intro = {
  eyebrow: 'Sign types',
  heading: 'Dimensional signage for logos, interiors, retail and events',
  paragraphs: [
    'We fabricate custom signs and branded display pieces where material, shape, depth and finish are part of the specification: a logo that stands off the wall, letters with real thickness, a mascot that’s part sign and part sculpture.',
    'Send production-ready artwork, or ask us to turn your logo into buildable geometry. One sign can combine several processes, such as a routed backer, a [laser-cut acrylic](/services/laser-cutting/) face, a printed graphic and a 3D printed detail, finished in paint or plating.',
  ],
}

const glance = {
  heading: 'Our custom 3D sign service at a glance',
  rows: [
    ['Studios', studios],
    [
      'Sign types',
      '3D logo signs, dimensional letters, retail and window displays, event signage, acrylic sign elements',
    ],
    [
      'Methods',
      '3D printing, CNC routing, laser-cut acrylic, UV printing, paint and metal plating',
    ],
    ['Size', 'Desk-size logo objects to large signs built in sections'],
    ['Artwork', 'A vector logo (AI, EPS, SVG or PDF) if you have one'],
    ['Price', 'Quoted per project after review'],
    [
      'Not included',
      'Installation, electrical work and permits, unless the quote lists them',
    ],
    [
      'Delivery',
      'Pickup by appointment, delivery in NYC, or shipping across the U.S.',
    ],
  ],
}

const work = {
  eyebrow: 'Project applications',
  heading: 'Custom 3D sign and display projects',
  photos: ['window', 'acrylic', 'platedLogo'],
}

const offer = {
  eyebrow: 'What we make',
  heading: 'What kinds of custom 3D signs can we make?',
  index: '02 / THE SIGNS',
  items: [
    {
      label: 'S1',
      title: '3D logo signs',
      body: 'Logos, symbols and mascots as raised or fully three-dimensional pieces for lobbies, studios, retail and branded sets.',
    },
    {
      label: 'S2',
      title: 'Dimensional letters',
      body: 'Letters, numbers and wordmarks with set depth, layered faces, matched paint and specialty finishes.',
    },
    {
      label: 'S3',
      title: 'Event and retail signage',
      body: 'Sign pieces for windows, pop-ups, launches, trade shows, sets and temporary installs.',
    },
    {
      label: 'S4',
      title: 'Acrylic sign elements',
      body: 'Laser-cut acrylic for clean faces, layered graphics, engraved detail and precise mounted parts.',
    },
    {
      label: 'S5',
      title: 'Large-format components',
      body: 'CNC-routed letters, backers and foam or wood parts for signs that need scale or thickness.',
    },
    {
      label: 'S6',
      title: 'Printed and plated finishes',
      body: 'Full-color UV printing, matched paint, texture or a metal plate over the fabricated part.',
    },
  ],
}

const contrast = {
  eyebrow: 'Fabrication methods',
  heading: 'Which process makes the best 3D signage?',
  paragraphs: [
    '3D printing suits sculptural logos, deep letterforms, curves and mascots that can’t be cut from sheet. CNC routing handles oversized letters, backers and thick wood or foam. Laser-cut acrylic gives crisp faces and layered graphics.',
    'We look at the whole sign (design, size, finish, mounting and where it hangs) before recommending the mix. Want it to light up? See our [custom neon signs](/neon-signs-nyc/).',
  ],
  links: [
    ['CNC routing', '/cnc-routing-service/'],
    ['Laser cutting in NYC', '/laser-cutting-service-nyc/'],
  ],
}

const steps = {
  eyebrow: 'Project process',
  heading: 'What happens after you send the project details',
  items: [
    {
      title: 'Send artwork and site details',
      body: 'Your logo or artwork, target size, photos of the location, quantity, deadline and use.',
    },
    {
      title: 'Review the fabrication approach',
      body: 'We review geometry, scale, finish, mounting, handling and budget, and suggest materials and methods.',
    },
    {
      title: 'Approve the scope and quote',
      body: 'The written quote lists materials, finish, quantity, hardware, packing, delivery and any installation coordination.',
    },
    {
      title: 'Fabricate, finish and hand off',
      body: 'Parts are made and finished as approved, then picked up, delivered or handed off as agreed.',
    },
  ],
}

const fit = {
  eyebrow: 'Is it the right fit?',
  heading: 'Is a fabrication studio right for your 3D sign?',
  good: [
    'Dimensional logo signs and lettering for lobbies, offices and studios',
    'Retail windows, pop-ups, launches and event signage',
    'Sculptural logos and mascots that can’t be cut from flat sheet',
    'Painted, UV-printed or metal-plated sign finishes',
  ],
  other: [
    'Permitted storefront signs, illuminated channel letters or electrical work: a licensed sign company',
    'Outdoor signs meant to last for years, unless we review the site and materials first',
    'Simple flat vinyl or printed panels, where a print shop costs less',
  ],
}

const pricing_ = {
  eyebrow: 'Budgeting your sign',
  heading: 'How much does a custom 3D sign cost?',
  lead: {
    label: 'Custom 3D signs are',
    value: 'Quoted per project',
    note: 'Send the logo, size, location photo and date for a written quote.',
  },
  rowsHeading: 'What sets the price of a 3D sign',
  rows: [
    [
      'Size and depth',
      'Bigger and deeper means more material and machine time',
    ],
    ['Material and method', '3D printing, CNC routing, acrylic, or a mix'],
    ['Artwork prep', 'A clean vector logo avoids rebuild time'],
    ['Finish', 'Paint, UV printing or metal plating'],
    ['Mounting and hardware', 'Standoffs, cleats or a freestanding base'],
    ['3D printed parts', `From about $${pricing.printFrom}, no setup fee`],
  ],
  note: 'Installation, permits and electrical work are separate unless your quote lists them.',
}

const logistics = {
  eyebrow: 'NYC pickup & delivery',
  heading: 'Brooklyn and Queens fabrication for NYC sign projects',
  paragraphs: [
    'We make 3D signs for NYC brands, agencies, artists, designers, producers and businesses in our studios at 616 Berriman Street, Brooklyn and 74-12 88th Street, Glendale, Queens. Being local helps with material reviews, samples, pickups and direct coordination.',
    'Pick up by appointment, get it delivered in NYC, or have it shipped across the U.S. Site visits and installation are scoped separately for each job.',
  ],
  aside: {
    eyebrow: 'Planning an installed sign?',
    title: 'Tell us about the wall first.',
    body: 'Share the wall construction, mounting height, access, power, landlord rules and any permit or engineering needs before quoting. The written quote says exactly what’s included.',
  },
}

const related = {
  heading: 'More sign and fabrication services',
  links: [
    ['Custom neon signs NYC', '/neon-signs-nyc/'],
    ['Laser cutting', '/services/laser-cutting/'],
    ['CNC routing', '/cnc-routing-service/'],
    ['UV printing', '/uv-printing-services/'],
  ],
}

const faqs = [
  {
    question: 'What kinds of custom 3D signs can PrintAWorld make in NYC?',
    answer:
      'Dimensional logo signs, raised lettering, branded display pieces, event signage, acrylic sign elements, sculptural brand marks and oversized sign components. Each project is reviewed for size, material, finish, mounting, location and use before the scope is confirmed.',
  },
  {
    question: 'Can you turn our existing logo into a 3D sign?',
    answer:
      'Yes. Send a vector logo if you have one, the target size, the depth you want, brand colors, reference images and a photo of the space. If the artwork needs to be rebuilt as production geometry, design time is included in the quote.',
  },
  {
    question: 'What materials can you use for dimensional signage?',
    answer:
      'Depending on the design: 3D printed resin or plastic, routed foam or wood, laser-cut acrylic, and mixed-material assemblies. Paint, UV printing and metal plating can finish the surface. The material depends on scale, detail, environment, handling, budget and mounting.',
  },
  {
    question: 'Do you make outdoor 3D signs?',
    answer:
      'Outdoor signs need extra review for weather, UV exposure, drainage, structure, mounting, local rules and maintenance. Not every printing material or finish suits outdoors. Send the location and how long it must last, and we’ll say whether it’s a fit.',
  },
  {
    question: 'Does the quote include installation or NYC sign permits?',
    answer:
      'Installation, engineering, landlord approval, electrical work and permits aren’t included automatically. Tell us what the site needs at the start; the written quote lists the fabrication, hardware, packing, delivery, installation coordination or third-party work that’s included.',
  },
  {
    question: 'How much does a custom 3D sign cost?',
    answer:
      'It’s quoted per project. Size, depth, material, artwork prep, quantity, structure, finish, mounting hardware, packing, delivery and deadline set the price. Send the logo, approximate size, a location photo and your date for a quote.',
  },
  {
    question: 'How long does custom signage fabrication take?',
    answer:
      'It depends on design readiness, approvals, scale, materials, finishing, quantity and logistics. Painted or plated work takes longer than a simple cut layer. Share a firm in-hand or event date before quoting so we can check it.',
  },
  {
    question: 'Can you fabricate a sign larger than your machines?',
    answer:
      'Often, yes. Large signs can be split into planned sections with alignment features, reinforced, assembled and finished so the seams are controlled. The approach depends on the geometry, viewing distance, handling, structure and installation plan.',
  },
]

const checklist = [
  'Your logo or artwork (vector if you have it)',
  'Target size and depth, and quantity',
  'Photos of the wall or space',
  'Finish and brand colors, or reference photos',
  'Mounting, indoor or outdoor, and your deadline',
]

module.exports = {
  key: 'signs3d',
  page,
  photos,
  intro,
  glance,
  work,
  offer,
  contrast,
  steps,
  fit,
  pricing: pricing_,
  logistics,
  related,
  faqs,
  faqHeading: 'Custom 3D signs NYC: common questions',
  checklist,
  quote: {
    eyebrow: 'Request a quote',
    heading: ['Tell us what you need', 'to build.'],
    body: 'Send your artwork, target size, photos of the location, quantity, finish references and deadline. We’ll review it and reply with a quote.',
  },
}
